import React, { useEffect, useMemo, useState } from 'react';
import SourcesIcon from '@/assets/icon-sources.svg?react';
import ChevronIcon from '@/assets/icon-cheveron.svg?react';
import WebIcon from '@/assets/icon-web.svg?react';
import FilesIcon from '@/assets/icon-path.svg?react';
import KbIcon from '@/assets/icon-knowledge-base.svg?react';
import './sources.scss';
import FileUploadBadge from './UI/FileUploadBadge';
import Favicon from './Favicon';

interface ChatSourcesProps {
  references?: CitationReference[];
}

const TYPE_ORDER = ['web', 'files', 'kb'];
const TYPE_LABELS: Record<string, string> = {
  web: 'Web',
  files: 'Files',
  kb: 'Knowledge Base',
};

const getDomain = (value?: string) => {
  if (!value) return '';
  try {
    return new URL(value).hostname.replace(/^www\./, '');
  } catch {
    return '';
  }
};

const capitalize = (value: string) => value.charAt(0).toUpperCase() + value.slice(1);

type SvgIconComponent = React.FC<React.SVGProps<SVGSVGElement>>;

const TYPE_ICON: Record<string, SvgIconComponent> = {
  web: WebIcon,
  files: FilesIcon,
  kb: KbIcon,
};

const renderTypeIcon = (type: string) => {
  const IconComp = TYPE_ICON[type] || FilesIcon;
  return <IconComp />;
};

const getReferenceType = (reference: CitationReference) => {
  return reference.type?.toLowerCase() || 'files';
};

const SourceCard = ({ reference }: { reference: CitationReference }) => {
  const type = getReferenceType(reference);
  const isWeb = type === 'web';
  const domain = isWeb ? getDomain(reference.url || reference.source) : '';

  const icon = isWeb ? (
    reference.favIcon ? (
      <Favicon key={reference.favIcon} src={reference.favIcon} />
    ) : (
      <WebIcon />
    )
  ) : type === 'kb' ? (
    <KbIcon />
  ) : (
    <FilesIcon />
  );

  const body = (
    <>
      <span className="source-card-line d-flex align-items-center">
        <span className="source-card-icon d-inline-flex align-items-center flex-shrink-0">
          {icon}
        </span>
        {isWeb ? (
          domain && <span className="source-card-domain text-truncate">{domain}</span>
        ) : (
          <span className="source-card-title source-card-title--inline text-truncate">
            {reference.title}
          </span>
        )}
      </span>
      {isWeb && <span className="source-card-title">{reference.title}</span>}
    </>
  );

  if (reference.url) {
    return (
      <a
        href={reference.url}
        target="_blank"
        rel="noopener noreferrer"
        className="source-card d-flex flex-column flex-grow-0 flex-shrink-0 gap-1 text-decoration-none text-reset"
        title={reference.title}
      >
        {body}
      </a>
    );
  }

  return (
    <div
      className="source-card d-flex flex-column flex-grow-0 flex-shrink-0 gap-1 p-2 text-reset"
      title={reference.title}
    >
      {body}
    </div>
  );
};

const SourceFileBadge = ({ reference }: { reference: CitationReference }) => (
  <div className="source-file-badge-link" title={reference.title}>
    <div className="chat-sources-file-badge">
      <FileUploadBadge uploadedFileTitle={reference.title} showRemoveButton={false} />
    </div>
  </div>
);

const getCardKey = (ref: CitationReference) => ref.id || ref.url || ref.source || ref.title;

const ChatSources = ({ references }: ChatSourcesProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeType, setActiveType] = useState('');

  const grouped = useMemo(() => {
    const map: Record<string, CitationReference[]> = {};
    (references || []).forEach((ref) => {
      const key = getReferenceType(ref);

      if (!map[key]) {
        map[key] = [];
      }

      map[key].push(ref);
    });
    return map;
  }, [references]);

  const availableTypes = useMemo(() => {
    const known = TYPE_ORDER.filter((type) => grouped[type]?.length);
    const unknown = Object.keys(grouped).filter((type) => !TYPE_ORDER.includes(type));
    return [...known, ...unknown];
  }, [grouped]);

  useEffect(() => {
    if (availableTypes.length > 0 && !availableTypes.includes(activeType)) {
      setActiveType(availableTypes[0]);
    }
  }, [availableTypes, activeType]);

  const handleWheel = (e: React.WheelEvent<HTMLDivElement>) => {
    if (e.deltaY === 0) return;
    e.currentTarget.scrollLeft += e.deltaY;
  };

  if (!references || references.length === 0) return null;

  const totalCount = references.length;
  const activeCards = grouped[activeType] || [];

  return (
    <div className="chat-sources">
      <button
        type="button"
        className="chat-sources-toggle d-inline-flex align-items-center gap-2 bg-transparent border-0 p-0"
        onClick={() => setIsOpen((prev) => !prev)}
        aria-expanded={isOpen}
        aria-controls="chat-sources-panel"
      >
        <span className="chat-sources-icon d-inline-flex align-items-center">
          <SourcesIcon />
        </span>
        <span className="chat-sources-label">Sources</span>
        <span className="chat-sources-count rounded-pill fw-bold">{totalCount}</span>
        <span className={`chat-sources-chevron d-inline-flex${isOpen ? ' is-open' : ''}`}>
          <ChevronIcon />
        </span>
      </button>

      {isOpen && (
        <div className="chat-sources-panel mt-2" id="chat-sources-panel">
          <div className="chat-sources-tabs d-flex gap-1 mb-2 overflow-x-auto" role="tablist">
            {availableTypes.map((type) => (
              <button
                key={type}
                type="button"
                role="tab"
                aria-selected={activeType === type}
                className={`chat-sources-tab d-inline-flex align-items-center bg-transparent text-nowrap${
                  activeType === type ? ' active' : ''
                }`}
                onClick={() => setActiveType(type)}
              >
                {renderTypeIcon(type)}
                <span>{TYPE_LABELS[type] || capitalize(type)}</span>
                <span className="chat-sources-tab-count rounded-pill">{grouped[type].length}</span>
              </button>
            ))}
          </div>

          <div
            className="chat-sources-cards-scroll d-flex align-items-start gap-2 overflow-x-auto"
            onWheel={handleWheel}
            role="tabpanel"
          >
            {activeCards.map((ref, idx) =>
              activeType === 'files' ? (
                <SourceFileBadge key={`${getCardKey(ref)}-${idx}`} reference={ref} />
              ) : (
                <SourceCard key={`${getCardKey(ref)}-${idx}`} reference={ref} />
              )
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default React.memo(ChatSources);
